import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Loader } from 'lucide-react'
import {
  getGetApiCompaniesCurrentServicemodulesQueryKey,
  getGetApiCompaniesCurrentServicemodulesUrlsServiceModuleIdQueryKey,
  useGetApiCompaniesCurrentServicemodulesUrlsServiceModuleId,
  usePatchApiCompaniesCurrentServicemodulesUrlsServiceModuleId,
} from '@/api/generated/company-management-user/company-management-user'
import type { ServiceModuleModel, UrlServiceModuleModel } from '@/api/model'
import { LocalizationEntityType } from '@/api/model/localizationEntityType'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { notify } from '@/lib/notify'
import { TranslateButton } from '@/components/common/translations/TranslateButton'
import { useEntityTranslations } from '@/components/common/translations/useEntityTranslations'
import { useStandardServiceModuleFields } from '../useStandardServiceModuleFields'
import { StandardModuleFields } from '../EditServiceModuleFields'
import { EditServiceModuleDialog } from '../EditServiceModuleDialog'
import { AutoTranslateAllButton } from '@/components/common/translations/AutoTranslateAllButton'

interface Props {
  module: ServiceModuleModel
  onClose: () => void
}

const TRANSLATABLE_PROPERTIES = ['url']

/** Prepends a scheme if none is present — otherwise the browser opens the URL relatively. */
function toHref(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

/**
 * Editor for URL modules: standard fields (column 1) + the `url` field (column 2)
 * with an "open" action. Saves everything in a single request via the URL endpoint.
 */
export function EditUrlServiceModule({ module, onClose }: Props) {
  const { t } = useTranslation(['service-modules-url', 'service-modules'])
  const std = useStandardServiceModuleFields(module)
  const [autoTranslateAll, setAutoTranslateAll] = useState(false)
  const queryClient = useQueryClient()
  const { data, isLoading } = useGetApiCompaniesCurrentServicemodulesUrlsServiceModuleId(module.id)
  const patch = usePatchApiCompaniesCurrentServicemodulesUrlsServiceModuleId()
  const translations = useEntityTranslations({
    entityType: LocalizationEntityType.ServiceModule,
    entityId: String(module.id),
    properties: TRANSLATABLE_PROPERTIES,
  })

  const detail = ((data as any)?.data ?? data) as UrlServiceModuleModel | undefined

  const [url, setUrl] = useState('')
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    if (detail && !loaded) {
      setUrl(detail.url ?? '')
      setLoaded(true)
    }
  }, [detail, loaded])

  function handleOpen() {
    const href = toHref(url)
    if (href) window.open(href, '_blank', 'noopener,noreferrer')
  }

  async function handleSave() {
    try {
      await patch.mutateAsync({
        serviceModuleId: module.id,
        data: { ...std.buildBasePayload(), url: url.trim() || null },
      })
      await std.saveTranslations()
      await translations.save(String(module.id))
      // url is intentionally excluded from auto-translation
      std.triggerTranslation(String(module.id), autoTranslateAll)
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetApiCompaniesCurrentServicemodulesUrlsServiceModuleIdQueryKey(module.id),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey(),
        }),
      ])
      notify.success(t('toasts.saved', { ns: 'service-modules' }))
      onClose()
    } catch (error) {
      notify.apiError(error, t('toasts.saveError', { ns: 'service-modules' }))
    }
  }

  const specific = (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="module-url">{t('editUrl.urlLabel')}</Label>
      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-muted-foreground">
          <Loader className="size-4 animate-spin text-primary" />
          <span className="text-sm">{t('editUrl.loading')}</span>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Input
            id="module-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            className="flex-1"
          />
          <Button variant="outline" onClick={handleOpen} disabled={!url.trim()}>
            <ExternalLink className="size-4 mr-1" />
            {t('editUrl.open')}
          </Button>
          {/* url is manually translated only — no plannedLanguages/autoTranslateAll */}
          <TranslateButton
            dialogTitle={t('editUrl.urlTranslateTitle')}
            value={translations.get('url')}
            onChange={(v) => translations.set('url', v)}
          />
        </div>
      )}
    </div>
  )

  return (
    <EditServiceModuleDialog
      id={module.id}
      moduleName={module.name}
      dialogTitle={t('editUrl.title')}
      onClose={onClose}
      standard={<StandardModuleFields fields={std} moduleType={module.type} autoTranslateAll={autoTranslateAll} />}
      specific={specific}
      onSave={handleSave}
      isSaving={patch.isPending || !loaded}
      autoTranslateButton={
        <AutoTranslateAllButton
          properties={std.autoTranslateProperties}
          autoTranslateAll={autoTranslateAll}
          onAutoTranslateAllChange={setAutoTranslateAll}
        />
      }
    />
  )
}
